import { EncapsulatedFeatureKeys } from "@lyfegen-platform/graphql-schema/models";
import { DividerWithText, LyfeTooltip } from "@lyfegen-platform/react-common";
import { isEmptyValue, isSubArray, safeDivide } from "@lyfegen-platform/utils";
import { Box, Checkbox, IconButton, useTheme } from "@material-ui/core";
import { DeleteOutline, Undo } from "@material-ui/icons";
import OverflowTooltipTypography from "../../../../../components/OverflowTooltipTypography";
import { ChangeEvent, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import AdjustmentTooltip, { AdjustmentTooltipProps } from "../../../../../components/AdjustmentTooltip";
import { DataForAdjustmentProps } from "../../../../../components/draggable-window/DragabbleWindowRoot";
import Adjust from "../../../../../components/icons/Adjust";
import { LiteCard } from "../../../../../components/lite/LiteCard";
import MappedComponent from "../../../../../components/mapped-components/MappedComponent";
import {
	BaseAgreement,
	DocumentStatus,
	DocumentType,
	FinancialDataPointStatus,
	SmartMipDocumentStatus,
	SmartMipPositionStatus,
} from "../../../../../models/generated/graphql";
import { userOrganization } from "../../../../../store/layout";
import { TableColumns } from "../../../../../store/lyfe-data-table";
import { userPersonalInfoState } from "../../../../../store/user";
import renderCurrency from "../../../../../utils/currency";
import formatCurrency from "../../../../../utils/currency-format";
import { localeDate } from "../../../../../utils/locale-date";
import CaselessRefundRelatedConditions from "../../../refunds/CaselessRefundRelatedConditions";
import RefundRelatedConditions, { IFinancialDataPoint } from "../../../refunds/RefundRelatedConditions";
import { InvokeCommentsButton } from "../../messaging/InvokeCommentsButton";
import AcceptanceStatus from "../decisions/AcceptanceStatus";
import { useDocumentContext } from "../DocumentContextProvider";
import { GetInvoiceQuery } from "../types/queries.generated";
import getRefundInfo, { RefundState } from "../utils/get-refund-info";
import { DocumentRefundGroup, RefundWithState } from "../utils/map-values";
import refundStyles from "./refund-styles";
import RefundsTableHeader from "./RefundsTableHeader";

interface ISelectedRefundProps {
	refundId: string;
}

const SelectRefund = (props: ISelectedRefundProps) => {
	const { selectedRefundIds, setSelectedRefundIds } = useDocumentContext();
	const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
		if (checked) {
			setSelectedRefundIds([...selectedRefundIds, props.refundId]);
		} else {
			setSelectedRefundIds(selectedRefundIds.filter((selectedRefund) => selectedRefund !== props.refundId));
		}
	};

	return <Checkbox checked={selectedRefundIds.includes(props.refundId)} onChange={handleChange} />;
};

type RefundTableColumn = TableColumns<keyof ReturnType<typeof getRefundInfo>> & { alignment: "left" | "right" };

export interface IRoleInfoProps {
	isCreator: boolean;
	isReviewer: boolean;
	isWaitingForApproval: boolean;
}

interface ITableRowProps {
	columns: Array<RefundTableColumn>;
	invoiceId: string;
	documentType: DocumentType;
	refund: GetInvoiceQuery["documents"][0]["documentRefundGroups"][0]["refunds"][0];
	caseInstance: GetInvoiceQuery["documents"][0]["documentRefundGroups"][0]["case"];
	currencyCode: string | null | undefined;
	isSentForExternalReview?: boolean;
	areActionsBlockedBySmartMipStatus: boolean;
	isAcceptanceDisabledForExternalParty?: boolean;
	agreementId: string;
	isCreator: boolean;
	isInReview?: boolean;
	isInDraft?: boolean;
	roleInfo?: IRoleInfoProps;
	areDecisionsLocked?: boolean;
	handleToggleRefund?: (id: string) => void;
	showLoader?: (isSaving: boolean) => void;
	isExposure?: boolean;
	reviewingParty?: string;
	isCaseless?: boolean;
	canAdjustByDocument?: boolean;
	canUpdate: boolean;
	handleOpenAdjustmentWindow: (row: any) => void;
	currentlyAdjustedRefundId?: string | null;
	hasInsuranceClaimIdAttribute?: boolean;
	hasInvoiceNumberAttribute?: boolean;
	isDeleted: boolean;
}

export function getTransactionInDocumentTableColumns(
	dateAtributeNames: Array<string>,
	documentType: DocumentType,
	translate: (key: string) => string
): Array<RefundTableColumn> {
	return [
		{
			title: dateAtributeNames.map((name) => translate(name)).join("/"),
			field: "date",
			default: true,
			alignment: "left",
		},
		{ title: "predecessorIds", field: "predecessorIds", alignment: "left", default: false },
		{ title: "successorIds", field: "successorIds", alignment: "left", default: false },
		{ title: "purchaseId", field: "purchaseId", default: false, alignment: "left" },
		{ title: "transactionId", field: "refundId", default: false, alignment: "left" },
		{ title: "wasRemoved", field: "wasRemoved", default: false, alignment: "left" },
		{ title: "insuranceClaimId", field: "insuranceClaimId", alignment: "left", default: true },
		{ title: "is-delta", field: "isDelta", alignment: "left", default: false },
		{ title: "pack", field: "pack", alignment: "left", default: true },
		documentType !== DocumentType.FreeOfCharge && { title: "quantity", field: "quantity", alignment: "right", default: true },
		{ title: "basePrice", field: "basePrice", alignment: "right", default: true },
		{ title: "grossAmount", field: "grossAmount", alignment: "right", default: false },
		{ title: "refundRate", field: "refundRate", alignment: "right", default: true },
		{ title: "refundAmount", field: "refundAmount", default: true, alignment: "right" },
		{ title: "externalFormId", field: "externalFormId", default: false, aligment: "right" },
	].filter((i): i is RefundTableColumn => Boolean(i));
}

const Row = (props: ITableRowProps) => {
	const {
		columns,
		refund,
		caseInstance,
		agreementId,
		isInDraft,
		currencyCode,
		documentType,
		invoiceId,
		roleInfo,
		areDecisionsLocked,
		handleToggleRefund,
		showLoader,
		isSentForExternalReview,
		areActionsBlockedBySmartMipStatus,
		isAcceptanceDisabledForExternalParty,
		isExposure,
		reviewingParty,
		isCaseless,
		canAdjustByDocument,
		handleOpenAdjustmentWindow,
		currentlyAdjustedRefundId,
		isDeleted,
	} = props;
	const { palette } = useTheme();
	const { alignRight, alignCenter, alignLeft, adjustedTransactions } = refundStyles();
	const { t } = useTranslation();
	const edge = refund.documentFinancialDataPointConnection.edges[0];
	const dataPoint = edge.node;
	const wasRemovedInReview = refund.documentFinancialDataPointConnection.edges[0].status === FinancialDataPointStatus.Removed;
	const refundInfo = getRefundInfo(refund as RefundWithState, t, currencyCode, caseInstance);
	const isAdjusted = !!refund.adjustedAmount;
	const canAdjust =
		canAdjustByDocument && !isDeleted && (!edge.smartMipStatus || edge.smartMipStatus === SmartMipPositionStatus.FurtherEnquiry);

	const getRate = () => {
		if (!refund.adjustedAmount || !refund.adjustedRate) return dataPoint.refundablePercentage;

		return refund.adjustedRate;
	};

	const visibleRefundAmount = isAdjusted ? renderCurrency(refund.adjustedAmount, currencyCode, 2, 2) : refundInfo.refundAmount;

	const adjustmentTooltipProps =
		isAdjusted && !!refund?.adjustedAmount
			? ({
					wasAdjustedBefore: true,
					adjustedAmount: refund?.adjustedAmount,
					adjustedRate: refund?.adjustedRate,
					originalAmount: dataPoint.refundable,
					currency: currencyCode || "CHF",
					lastAdjustedAt: localeDate(refund?.lastAdjustedAt as string),
					adjustedBy: refund?.adjustedBy ? `${refund?.adjustedBy?.firstName} ${refund?.adjustedBy?.lastName}` : "",
					adjustmentComment: refund?.adjustmentComment || "",
					canUserViewFinancials: true,
			  } as AdjustmentTooltipProps)
			: { wasAdjustedBefore: false, canUserViewFinancials: false };

	const renderValue = (column: RefundTableColumn, key: number) => {
		switch (column.field) {
			case "refundAmount":
				return (
					<td className={alignRight}>
						<MappedComponent
							component="Tooltip"
							props={{
								title: isAdjusted ? <AdjustmentTooltip {...adjustmentTooltipProps} /> : "",
								placement: "bottom-end",
							}}
						>
							<Box display="flex" justifyContent="flex-end" className={isAdjusted ? adjustedTransactions : ""}>
								{isAdjusted && (
									<span>
										<Adjust fill={palette.text.primary} />
									</span>
								)}
								<OverflowTooltipTypography {...(isDeleted ? { style: { textDecoration: "line-through" } } : undefined)}>
									{visibleRefundAmount}
								</OverflowTooltipTypography>
							</Box>
						</MappedComponent>
					</td>
				);

			case "refundRate":
				return (
					<td className={alignRight}>
						<MappedComponent
							component="Tooltip"
							props={{
								title: isAdjusted ? <AdjustmentTooltip {...adjustmentTooltipProps} /> : "",
								placement: "bottom-end",
							}}
						>
							<Box
								display="flex"
								justifyContent="flex-end"
								style={{ gap: 10 }}
								className={isAdjusted ? adjustedTransactions : undefined}
							>
								{isAdjusted && <Adjust fill={palette.text.primary} />}
								<OverflowTooltipTypography>
									{isEmptyValue(getRate())
										? `${formatCurrency(
												safeDivide(dataPoint.refundable, dataPoint.basePrice * dataPoint.quantity) * 100 || 0,
												2,
												2
										  )}%` //"fixed-price"
										: `${formatCurrency(getRate(), 2, 2)}%`}
								</OverflowTooltipTypography>
							</Box>
						</MappedComponent>
					</td>
				);

			default:
				return (
					<td
						key={key}
						style={{
							width: "100%",
							verticalAlign: "middle",
						}}
					>
						<OverflowTooltipTypography align={column.alignment}>{refundInfo[column.field]}</OverflowTooltipTypography>
					</td>
				);
		}
	};

	return (
		<tr key={refund.id}>
			<td className={alignCenter}>
				<SelectRefund refundId={refund.id} />
			</td>
			<td className={alignCenter}>
				{isCaseless ? (
					<CaselessRefundRelatedConditions
						agreementId={agreementId}
						financialDataPoint={dataPoint as IFinancialDataPoint}
						caseInstance={null}
					/>
				) : (
					<RefundRelatedConditions
						agreementId={agreementId}
						financialDataPoint={dataPoint as IFinancialDataPoint}
						caseInstance={caseInstance}
					/>
				)}
			</td>
			{columns.map((c, i) => renderValue(c, i))}

			<td className={alignLeft}>
				{
					<AcceptanceStatus
						id={dataPoint.generatedUuid}
						documentType={documentType}
						invoiceId={invoiceId}
						amount={dataPoint.refundable}
						adjustedAmount={refund.adjustedAmount}
						roleInfo={roleInfo}
						canUpdate={props.canUpdate}
						isAcceptanceDisabledForExternalParty={isAcceptanceDisabledForExternalParty}
						isSentForExternalReview={isSentForExternalReview}
						areActionsBlockedBySmartMipStatus={areActionsBlockedBySmartMipStatus}
						showLoader={showLoader}
						areDecisionsLocked={areDecisionsLocked}
						isExposure={isExposure}
						reviewingParty={reviewingParty}
					/>
				}
			</td>
			<td className={alignCenter}>
				{canAdjust && (
					<MappedComponent
						component="Tooltip"
						props={{
							title: t("document-transaction-adjust"),
							placement: "bottom-end",
						}}
					>
						<IconButton
							onClick={() => handleOpenAdjustmentWindow(refund.id)}
							size="small"
							style={{ backgroundColor: currentlyAdjustedRefundId === refund.id ? palette.grey[300] : "" }}
						>
							<Adjust fill="#367bf5" />
						</IconButton>
					</MappedComponent>
				)}
			</td>
			{isInDraft && !wasRemovedInReview && (
				<td className={alignCenter}>
					<LyfeTooltip title={t(isDeleted ? "undo" : "delete")}>
						<IconButton aria-label={isDeleted ? "undo-deletion" : "delete-row"} onClick={() => handleToggleRefund?.(refund.id)}>
							{isDeleted ? <Undo color="primary" /> : <DeleteOutline color="primary" />}
						</IconButton>
					</LyfeTooltip>
				</td>
			)}
			<td className={alignCenter}>
				<InvokeCommentsButton caseId={caseInstance?.id} refundId={refund.id} />{" "}
			</td>
		</tr>
	);
};
const TableRow = memo(Row);

interface ISelectAllProps {
	refundIds: Array<string>;
}

const SelectAll = (props: ISelectAllProps) => {
	const { selectedRefundIds, setSelectedRefundIds } = useDocumentContext();
	const isChecked = isSubArray(selectedRefundIds, props.refundIds);

	const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
		if (checked) {
			setSelectedRefundIds([...selectedRefundIds, ...props.refundIds]);
		} else {
			setSelectedRefundIds(selectedRefundIds.filter((selectedRefund) => !props.refundIds.includes(selectedRefund)));
		}
	};

	return <Checkbox checked={isChecked} onChange={handleChange} />;
};

interface ITableHeadProps extends ISelectAllProps {
	isInReview?: boolean;
	columns: Array<RefundTableColumn>;
}

const TableHead = (props: ITableHeadProps) => {
	const { alignLeft, alignCenter } = refundStyles();
	const { t } = useTranslation();

	return (
		<thead>
			<tr>
				<th className={alignCenter}>
					<SelectAll refundIds={props.refundIds} />
				</th>
				<th></th>
				{props.columns.map((c, i) => (
					<th key={i} style={{ verticalAlign: "middle" }}>
						<OverflowTooltipTypography align={c.alignment} variant="body1" style={{ fontWeight: 600 }}>
							{t(c.title)}
						</OverflowTooltipTypography>
					</th>
				))}
				{/* Optional styling for adjusted: */}
				<th className={alignLeft}>
					<OverflowTooltipTypography variant="body1" style={{ fontWeight: 600 }}>
						{t("decision")}
					</OverflowTooltipTypography>
				</th>
				<th></th>
				<th></th>
			</tr>
		</thead>
	);
};

interface IRefundsTableProps {
	documentRefundGroup: DocumentRefundGroup;
	baseAgreements: Array<BaseAgreement>;
	currencyCode: string | null | undefined;
	invoiceId: string;
	dateAttributeNames: Array<string>;
	documentType: DocumentType;
	status?: DocumentStatus | null;
	roleInfo?: IRoleInfoProps;
	isSentForExternalReview?: boolean;
	isSentToSmartMip: boolean;
	smartMipStatus?: SmartMipDocumentStatus | null;
	isAcceptanceDisabledForExternalParty?: boolean;
	isExposure?: boolean;
	areDecisionsLocked?: boolean;
	handleToggleRefund?: (id: string) => void;
	showLoader?: (isSaving: boolean) => void;
	reviewingParty?: string;
	reviewerParty?: string;
	isConfirmedByReviewer: boolean;
	isAdjustmentWindowOpen: boolean;
	setIsAdjustmentWindowOpen: (refundId: string) => void;
	currentlyAdjustedRefund?: DataForAdjustmentProps | null;
	canManageRefunds?: boolean;
}

const RefundsTable = (props: IRefundsTableProps) => {
	const { t } = useTranslation();
	const { tableBody, table, cardRoot } = refundStyles();
	const isInReview = props.status === DocumentStatus.InReview || props.status === DocumentStatus.InApproval;
	const isInDraft = props.status === DocumentStatus.WorkingDraft;
	const refundIds = props.documentRefundGroup.refunds.map((refund) => refund.id);
	const isCaseless = !props.documentRefundGroup.case && props.documentRefundGroup.agreement?.isCaseless;
	const organization = useRecoilValue(userOrganization);
	const userInfo = useRecoilValue(userPersonalInfoState);

	const getEnabledColumnNames = () => {
		switch (props.documentType) {
			case DocumentType.CreditNote:
				return userInfo.transactionsInCreditNoteDocumentTableColumns;
			case DocumentType.Invoice:
				return userInfo.transactionsInRefundInvoiceDocumentTableColumns;
			case DocumentType.FreeOfCharge:
				return userInfo.transactionsInFreeOfChargeDocumentTableColumns;
		}
	};

	const enabledColumnNames = getEnabledColumnNames();

	const columns = useMemo(
		() =>
			getTransactionInDocumentTableColumns(props.dateAttributeNames, props.documentType, t).filter((c) =>
				enabledColumnNames.includes(c.field)
			),
		[enabledColumnNames]
	);
	const isAdjustingEnabledByTier = organization.encapsulatedFeatures.some(
		(feature) => feature.isEnabled && feature.ofType.key === EncapsulatedFeatureKeys.DocumentAdjustment
	);
	const { setIsAdjustmentWindowOpen, isAdjustmentWindowOpen } = props;
	const areActionsBlockedBySmartMipStatus =
		!!props.isSentToSmartMip && (props.smartMipStatus !== SmartMipDocumentStatus.FurtherEnquiry || !!props.roleInfo?.isReviewer);
	const canAdjustByDocument =
		isAdjustingEnabledByTier &&
		!areActionsBlockedBySmartMipStatus &&
		((props.roleInfo?.isReviewer && isInReview && !props.isConfirmedByReviewer) ||
			(props.roleInfo?.isCreator && (isInReview || isInDraft))) &&
		props.canManageRefunds;
	const hasInsuranceClaimIdAttribute = props.documentRefundGroup.refunds.some((refund) =>
		refund.documentFinancialDataPointConnection.edges.some((edge) => !!edge.node.evaluates.insuranceClaimId)
	);
	const hasInvoiceNumberAttribute = props.documentRefundGroup.refunds.some((refund) =>
		refund.documentFinancialDataPointConnection.edges.some((edge) => !!edge.node.evaluates.invoiceNumber)
	);

	const handleOpenAdjustmentWindow = (id: string) => {
		if (isAdjustmentWindowOpen) {
			setIsAdjustmentWindowOpen("");
		}

		if (!id) {
			return;
		}

		setIsAdjustmentWindowOpen(id);
	};

	const canUpdate = isInReview && ((props.roleInfo?.isReviewer && !props.isConfirmedByReviewer) || props.roleInfo?.isCreator);

	return (
		<LiteCard className={cardRoot}>
			<RefundsTableHeader
				documentStatus={props.status}
				documentRefundGroup={props.documentRefundGroup}
				baseAgreements={props.baseAgreements}
				isExposure={props.isExposure}
				externalFlowDocumentId={props.invoiceId}
			/>
			<div style={{ width: "100%" }}>
				<DividerWithText text="" />
			</div>
			<div style={{ width: "100%" }}>
				<table className={table}>
					<colgroup>
						<col style={{ width: 50 }} />
						<col style={{ width: 50 }} />
						{columns.map((_, i) => (
							<col key={i} />
						))}
						<col style={{ width: props.roleInfo?.isReviewer && !props.roleInfo.isWaitingForApproval ? 210 : 110 }} />
						<col style={{ width: 50 }} />
						{isInDraft && <col style={{ width: 50 }} />}
						<col style={{ width: 70, textAlign: "left" }} />
					</colgroup>
					<TableHead columns={columns} isInReview={isInReview} refundIds={refundIds} />
					<tbody className={tableBody}>
						{props.documentRefundGroup.refunds.map((refund) => (
							<TableRow
								key={refund.id}
								columns={columns}
								refund={refund}
								caseInstance={props.documentRefundGroup.case}
								isInReview={isInReview}
								isInDraft={isInDraft}
								isCreator={!!props.roleInfo?.isCreator}
								currencyCode={props.currencyCode}
								agreementId={
									props.documentRefundGroup.case?.actualAgreementId || props.documentRefundGroup.agreement?.id || ""
								}
								documentType={props.documentType}
								invoiceId={props.invoiceId}
								roleInfo={props.roleInfo}
								handleToggleRefund={props.handleToggleRefund}
								showLoader={props.showLoader}
								areDecisionsLocked={props.areDecisionsLocked}
								isExposure={props.isExposure}
								isAcceptanceDisabledForExternalParty={props.isAcceptanceDisabledForExternalParty}
								isSentForExternalReview={props.isSentForExternalReview}
								areActionsBlockedBySmartMipStatus={areActionsBlockedBySmartMipStatus}
								reviewingParty={props.reviewingParty}
								isCaseless={isCaseless}
								canAdjustByDocument={canAdjustByDocument}
								canUpdate={!!canUpdate}
								handleOpenAdjustmentWindow={handleOpenAdjustmentWindow}
								currentlyAdjustedRefundId={props?.currentlyAdjustedRefund?.refundId}
								hasInsuranceClaimIdAttribute={hasInsuranceClaimIdAttribute}
								hasInvoiceNumberAttribute={hasInvoiceNumberAttribute}
								isDeleted={refund.state === RefundState.Deleted}
							/>
						))}
					</tbody>
				</table>
			</div>
		</LiteCard>
	);
};

export default memo(RefundsTable);
